/**
 * Iter 84 — Consent Phase 3: Post-procedure complication audit log.
 *
 * Inline panel rendered on the consent submission detail page. Lets any
 * staff member viewing the submission log a complication (radiographers /
 * nurses are usually the ones who witness it). PMs can edit outcomes and
 * close the loop. Admins can delete records.
 *
 * Backend: /api/consents/{submission_id}/complications  (POST + GET)
 *          /api/complications/{complication_id}         (PATCH + DELETE)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, ChevronDown, Trash2, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  infection:        'Infection',
  bleeding:         'Bleeding / haematoma',
  nerve:            'Nerve injury / paraesthesia',
  vascular:         'Vascular injury',
  allergic:         'Allergic reaction',
  vasovagal:        'Vasovagal',
  pain_flare:       'Pain flare',
  hyperglycaemia:   'Hyperglycaemia (post-steroid)',
  skin:             'Skin (atrophy / depigmentation)',
  tendon:           'Tendon rupture / partial tear',
  failed_procedure: 'Failed procedure',
  other:            'Other',
};

const SEVERITY_COLOR = {
  mild:             { bg: '#16A34A15', fg: '#15803D' },
  moderate:         { bg: '#F59E0B15', fg: '#B45309' },
  severe:           { bg: '#EA580C15', fg: '#C2410C' },
  life_threatening: { bg: '#DC262615', fg: '#B91C1C' },
  fatal:            { bg: '#7F1D1D20', fg: '#7F1D1D' },
};

const OUTCOME_LABEL = {
  ongoing:           'Ongoing',
  resolved:          'Resolved',
  lost_to_followup:  'Lost to follow-up',
  fatal:             'Fatal',
};

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function ConsentComplicationsPanel({ submissionId, color = '#7C3AED' }) {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/consents/${submissionId}/complications`, { withCredentials: true });
      setRows(r.data || []);
    } catch (_e) { /* keep silent — endpoint protected, returns 401 only if signed out */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [submissionId]);

  return (
    <div className="print-hidden mb-5 p-4 bg-card border border-border rounded-2xl" data-testid="consent-complications-panel">
      <div className="flex items-center gap-2 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color }}>
          Post-procedure complications · audit log
        </p>
        <span className="h-px flex-1 bg-border" />
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${color}15`, color }}
              data-testid="consent-complications-count">
          {rows.length} {rows.length === 1 ? 'event' : 'events'}
        </span>
      </div>

      {!adding && (
        <button onClick={() => setAdding(true)}
          data-testid="consent-complication-add-btn"
          className="w-full text-xs font-semibold py-2 rounded-lg border border-dashed inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-muted/30"
          style={{ borderColor: `${color}60`, color }}>
          <Plus className="w-3.5 h-3.5" /> Log a post-procedure complication
        </button>
      )}

      {adding && (
        <AddComplicationForm submissionId={submissionId} color={color}
          onCancel={() => setAdding(false)}
          onAdded={() => { setAdding(false); load(); }} />
      )}

      {loading ? (
        <p className="text-xs text-muted-foreground mt-3">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-3" data-testid="consent-complications-empty">
          No complications recorded. Logged events trend on the Compliance hub + appear in the DIAS evidence pack.
        </p>
      ) : (
        <div className="space-y-2 mt-3" data-testid="consent-complications-list">
          {rows.map(r => (
            <ComplicationRow key={r.complication_id} row={r} color={color}
              canEdit={isPM} canDelete={isAdmin} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function AddComplicationForm({ submissionId, color, onCancel, onAdded }) {
  const [category, setCategory] = useState('pain_flare');
  const [severity, setSeverity] = useState('mild');
  const [onsetAt, setOnsetAt] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [outcome, setOutcome] = useState('ongoing');
  const [outcomeAt, setOutcomeAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (description.trim().length < 4) { setErr('Description is required (≥ 4 chars).'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/consents/${submissionId}/complications`,
        { category, severity, onset_at: onsetAt, description: description.trim(),
          action_taken: actionTaken.trim(), outcome, outcome_at: outcomeAt },
        { withCredentials: true });
      onAdded();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    }
    setBusy(false);
  };

  return (
    <div className="mt-1 p-3 rounded-lg border-2 space-y-2" style={{ borderColor: `${color}40`, backgroundColor: `${color}06` }}
         data-testid="consent-complication-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Category</span>
          <select value={category} onChange={e => setCategory(e.target.value)}
            data-testid="comp-category"
            className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-2.5 py-1.5">
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Severity</span>
          <select value={severity} onChange={e => setSeverity(e.target.value)}
            data-testid="comp-severity"
            className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-2.5 py-1.5">
            <option value="mild">Mild (1)</option>
            <option value="moderate">Moderate (2)</option>
            <option value="severe">Severe (3)</option>
            <option value="life_threatening">Life-threatening (4)</option>
            <option value="fatal">Fatal (5)</option>
          </select>
        </label>
      </div>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Onset</span>
        <input type="date" value={onsetAt} onChange={e => setOnsetAt(e.target.value)}
          data-testid="comp-onset-at"
          className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-2.5 py-1.5" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Description</span>
        <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)}
          data-testid="comp-description"
          placeholder="e.g. Patient developed L knee swelling + warmth 36h post-injection, no fever."
          className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-2.5 py-1.5" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Action taken</span>
        <textarea rows={2} value={actionTaken} onChange={e => setActionTaken(e.target.value)}
          data-testid="comp-action"
          placeholder="e.g. GP review same day, swab sent, oral fluclox started, follow-up in 7d."
          className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-2.5 py-1.5" />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Outcome</span>
          <select value={outcome} onChange={e => setOutcome(e.target.value)}
            data-testid="comp-outcome"
            className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-2.5 py-1.5">
            {Object.entries(OUTCOME_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        {outcome !== 'ongoing' && (
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Closed</span>
            <input type="date" value={outcomeAt} onChange={e => setOutcomeAt(e.target.value)}
              data-testid="comp-outcome-at"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-2.5 py-1.5" />
          </label>
        )}
      </div>
      {err && <p className="text-[11px] text-red-600" data-testid="comp-error">{err}</p>}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button onClick={onCancel} disabled={busy}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border">Cancel</button>
        <button onClick={submit} disabled={busy}
          data-testid="comp-save-btn"
          className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-50"
          style={{ backgroundColor: color }}>
          {busy ? 'Saving…' : 'Log complication'}
        </button>
      </div>
    </div>
  );
}

function ComplicationRow({ row, color, canEdit, canDelete, onChanged }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState(row.outcome);
  const [busy, setBusy] = useState(false);
  const sev = SEVERITY_COLOR[row.severity] || SEVERITY_COLOR.mild;
  const catLabel = CATEGORY_LABELS[row.category] || row.category;

  const updateOutcome = async (newOutcome) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/complications/${row.complication_id}`,
        { outcome: newOutcome, outcome_at: new Date().toISOString().slice(0, 10) },
        { withCredentials: true });
      setOutcome(newOutcome);
      onChanged();
    } catch (_e) { /* ignore */ }
    setBusy(false);
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this complication record permanently?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/complications/${row.complication_id}`, { withCredentials: true });
      onChanged();
    } catch (_e) { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="border border-border rounded-lg p-2.5"
         data-testid={`comp-row-${row.complication_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
              style={{ backgroundColor: sev.bg, color: sev.fg }}>{row.severity.replace('_', ' ')}</span>
        <span className="text-xs font-semibold text-foreground flex-1 truncate">{catLabel}</span>
        <span className="text-[10px] text-muted-foreground">
          {row.onset_at?.slice(0, 10) || ''}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
              style={outcome === 'ongoing'
                ? { backgroundColor: '#F59E0B15', color: '#B45309' }
                : { backgroundColor: '#16A34A15', color: '#15803D' }}>
          {OUTCOME_LABEL[outcome] || outcome}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-border space-y-1.5">
          <p className="text-xs text-foreground"><strong>Description:</strong> {row.description}</p>
          {row.action_taken && (
            <p className="text-xs text-foreground"><strong>Action taken:</strong> {row.action_taken}</p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Reported by <strong>{row.reported_by?.name || 'unknown'}</strong>
            {row.reported_at ? ` · ${new Date(row.reported_at).toLocaleString('en-AU')}` : ''}
            {row.closed_at ? ` · closed ${new Date(row.closed_at).toLocaleString('en-AU')}` : ''}
          </p>
          {canEdit && outcome === 'ongoing' && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Close as:</span>
              <button onClick={() => updateOutcome('resolved')} disabled={busy}
                data-testid={`comp-resolve-${row.complication_id}`}
                className="text-[11px] font-semibold px-2 py-1 rounded border inline-flex items-center gap-1"
                style={{ borderColor: '#16A34A40', color: '#15803D' }}>
                <CheckCircle2 className="w-3 h-3" /> Resolved
              </button>
              <button onClick={() => updateOutcome('lost_to_followup')} disabled={busy}
                data-testid={`comp-lost-${row.complication_id}`}
                className="text-[11px] font-semibold px-2 py-1 rounded border border-border text-muted-foreground">
                Lost to follow-up
              </button>
              {canDelete && (
                <button onClick={onDelete} disabled={busy}
                  data-testid={`comp-delete-${row.complication_id}`}
                  className="ml-auto text-[10px] font-semibold px-2 py-1 rounded text-red-500 hover:bg-red-500/10 inline-flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              )}
            </div>
          )}
          {canDelete && outcome !== 'ongoing' && (
            <button onClick={onDelete} disabled={busy}
              data-testid={`comp-delete-${row.complication_id}`}
              className="text-[10px] font-semibold px-2 py-1 rounded text-red-500 hover:bg-red-500/10 inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Delete record
            </button>
          )}
        </div>
      )}
      {!open && row.severity === 'life_threatening' && (
        <p className="text-[10px] mt-1 inline-flex items-center gap-1" style={{ color: '#B91C1C' }}>
          <AlertTriangle className="w-3 h-3" /> Life-threatening — see detail
        </p>
      )}
    </div>
  );
}
